import { c as _c } from "react/compiler-runtime";
import { createContext, type ReactNode, useContext, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';

// Named card sizes → px width. Declare intent (how wide one card should be) and
// GalleryGrid computes how many columns fit the 1280 viewport. Pick by the
// component's natural width:
//   compact — chips, badges, rows, small panels (packs ~3-up)
//   medium  — forms, cards, list popups (2-up)
//   wide    — chat threads, tables, markdown, doc previews (1-up, roomy)
//   full    — page shells, canvases, nav (1-up, full bleed)
export type GallerySize = 'compact' | 'medium' | 'wide' | 'full';
const SIZE_WIDTH: Record<GallerySize, number> = {
  compact: 360,
  medium: 560,
  wide: 760,
  full: 1180
};
const GAP = 32; // gap-8
// Usable width inside the 1280 viewport, minus the p-8 wrapper padding on both sides.
const USABLE = 1280 - 2 * GAP;
const columnsFor = (width: number) => Math.max(1, Math.floor((USABLE + GAP) / (width + GAP)));

// Spread into each gallery story's `meta.parameters`: pins the 1280 viewport the
// column math above assumes.
export const galleryParameters = {
  viewport: {
    defaultViewport: 1280
  }
};

// Lets GalleryCard inherit the grid's size so authors set it once, on the grid.
const GallerySizeContext = createContext<GallerySize | undefined>(undefined);

// Scopes a page component's NavigationHeader portal to one card: without it, page
// screens that portal into a shared global target all collide on that one node.
// Undefined outside a gallery, so the real app is unaffected.
const GalleryPortalContext = createContext<string | undefined>(undefined);
export const useGalleryPortalId = () => {
  return useContext(GalleryPortalContext);
};
type GalleryGridProps = {
  children: ReactNode;
  // Omit to auto-fit compact cards (as many as fit the row). Set a named size
  // when the component needs more room; the column count is derived, not hand-picked.
  size?: GallerySize;
};

// Portalling into <body> + hiding #storybook-root leaves only the grid in the page. The muted
// backdrop lets the white elevation cards read as raised.
export const GalleryGrid = t0 => {
  const $ = _c(11);
  const {
    children,
    size
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  useLayoutEffect(_temp, t1);
  let t2;
  if ($[1] !== size) {
    t2 = size ? `repeat(${columnsFor(SIZE_WIDTH[size])}, ${SIZE_WIDTH[size]}px)` : `repeat(auto-fill, ${SIZE_WIDTH.compact}px)`;
    $[1] = size;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const gridTemplateColumns = t2;
  let t3;
  if ($[3] !== gridTemplateColumns) {
    t3 = {
      gridTemplateColumns
    };
    $[3] = gridTemplateColumns;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== children || $[6] !== t3) {
    t4 = <div className="grid gap-8 items-start" style={t3}>{children}</div>;
    $[5] = children;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== size || $[9] !== t4) {
    t5 = createPortal(<div className="w-fit min-w-screen bg-secondary p-8"><GallerySizeContext.Provider value={size}>{t4}</GallerySizeContext.Provider></div>, document.body);
    $[8] = size;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
};
type GalleryCardProps = {
  label: string;
  children: ReactNode;
  // Overrides the card size for a single card; defaults to the grid's size.
  size?: GallerySize;
  // Set to scope a page component's NavigationHeader portal to this card; omit for
  // components with no such portal. See GalleryPortalContext.
  portalId?: string;
};
export const GalleryCard = t0 => {
  const $ = _c(13);
  const {
    label,
    children,
    size,
    portalId
  } = t0;
  const gridSize = useContext(GallerySizeContext);
  const width = SIZE_WIDTH[size ?? gridSize ?? "compact"];
  let t1;
  if ($[0] !== width) {
    t1 = {
      width
    };
    $[0] = width;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== label) {
    t2 = <span className="text-caption-2 text-tertiary">{label}</span>;
    $[2] = label;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== children || $[5] !== portalId) {
    t3 = portalId ? <GalleryPortalContext.Provider value={portalId}><header id={portalId} className="relative" />{children}</GalleryPortalContext.Provider> : children;
    $[4] = children;
    $[5] = portalId;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t3) {
    t4 = <div className="relative bg-primary rounded-default border border-tertiary shadow-[0px_6px_12px_0px_rgba(5,5,5,0.18)] overflow-hidden p-4">{t3}</div>;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t1 || $[10] !== t2 || $[11] !== t4) {
    t5 = <div className="flex flex-col gap-2" style={t1}>{t2}{t4}</div>;
    $[9] = t1;
    $[10] = t2;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return t5;
};
function _temp() {
  const root = document.getElementById("storybook-root");
  if (!root) {
    return;
  }
  const prev = root.style.display;
  root.style.display = "none";
  return () => {
    root.style.display = prev;
  };
}